import type { WritePolicy } from './api';

// May the Confirm button be pressed?
//
// ⚠ `null` MEANS UNKNOWN, NOT ALLOWED, and that distinction is the whole file.
// The write-policy fetch sets the policy to `null` on failure, and the button
// used to treat only `policy?.disabled === true` as blocking — so a policy that
// failed to load read exactly like a healthy writable one. The product then
// invited a customer to confirm a write the server was going to refuse.
//
// It pairs badly with everything else: the refusal arrives from an API call
// whose error used to render on a screen the user had already left, and the
// customer's own reading of it is that the product is broken rather than that
// they are not entitled.
//
// So an unknown policy blocks. The cost is a disabled button during a blip; the
// cost of the other choice is a write attempt that fails after the customer
// committed to it.

export type WriteGate =
  | { allowed: true }
  | { allowed: false; because: 'unknown' | 'blocked' | 'nothing-to-do' | 'too-many' };

export interface WriteGateInput {
  policy: WritePolicy | null;
  /**
   * How many changes this submission would make.
   *
   * ⚠ `null` means "not known yet", which is the honest answer on the merge
   * screen: the change set is built by the server from the surviving record,
   * the retired one and whichever fields the reviewer carried over, so the
   * browser cannot count it without reimplementing buildMergePlan. The cap is
   * then enforced server-side with the real number and its own message, rather
   * than guessed at here and guessed wrong.
   */
  changeCount: number | null;
}

export function writeGate({ policy, changeCount }: WriteGateInput): WriteGate {
  // Unknown first: without a policy we cannot judge the count either, and
  // "nothing to do" would be a misleading reason to show.
  if (policy === null) return { allowed: false, because: 'unknown' };
  if (policy.disabled) return { allowed: false, because: 'blocked' };
  // An unknown count is allowed through to the server, which knows it.
  if (changeCount === null) return { allowed: true };

  if (changeCount <= 0) return { allowed: false, because: 'nothing-to-do' };

  // The first-job cap. It lifts by itself once an edit job has completed and
  // not been undone, so this is a "not yet", not a refusal.
  if (changeCount > policy.maxChanges) return { allowed: false, because: 'too-many' };

  return { allowed: true };
}

/** Convenience for a disabled={…} prop. */
export function canConfirm(input: WriteGateInput): boolean {
  return writeGate(input).allowed;
}
